import type { TranslationKey } from "@/translations"

/**
 * Everyday labels for the outgoing-payload (ProviderReadyWorker) fields —
 * shared by the detail dialog's field/value display and the edit form so the
 * two never drift.
 *
 * The labels themselves live in translations/index.ts under the `field.` prefix
 * (so both languages stay in one place); this map is the payload field name →
 * translation key lookup. Use `payloadFieldLabel` rather than indexing it
 * directly, so an unknown field falls back to its raw name instead of
 * rendering "undefined".
 */
export const PAYLOAD_FIELD_LABEL_KEYS: Record<string, TranslationKey> = {
  userProfileId: "field.userProfileId",
  providerEmployeeId: "field.providerEmployeeId",
  category: "field.category",
  firstName: "field.firstName",
  lastName: "field.lastName",
  middleName: "field.middleName",
  email: "field.email",
  profileTypeValue: "field.profileTypeValue",
  // Legacy payloads saved before the display-value change still carry the raw code.
  profileTypeCode: "field.profileTypeCode",
  hireDate: "field.hireDate",
  payType: "field.payType",
  hourlyRate: "field.hourlyRate",
  normalHours: "field.normalHours",
  department: "field.department",
  workersCompCode: "field.workersCompCode",
  workLocation: "field.workLocation",
  ein: "field.ein",
  internalOrganizationId: "field.internalOrganizationId",
  internalOrganizationCode: "field.internalOrganizationCode",
  providerClientId: "field.providerClientId",
  legalCode: "field.legalCode",
  payGroup: "field.payGroup",
  jobTitle: "field.jobTitle",
  address1: "field.address1",
  address2: "field.address2",
  city: "field.city",
  provinceOrState: "field.provinceOrState",
  postalOrZip: "field.postalOrZip",
  countryCode: "field.countryCode",
  homePhone: "field.homePhone",
  mobilePhone: "field.mobilePhone",
  additionalFields: "field.additionalFields",
  organizationFields: "field.organizationFields",
}

/** The label for one payload field, or the raw field name when it has none. */
export function payloadFieldLabel(field: string, t: (key: TranslationKey) => string): string {
  const key = PAYLOAD_FIELD_LABEL_KEYS[field]
  return key ? t(key) : field
}

// payloadToRows/categoryLabel were removed with the detail panel's field-by-field
// view of the PREPARED payload. That panel now shows only the provider's wire
// body — what was sent, or what would be — so a helper that renders the neutral
// payload for display would only invite the same confusion back. The labels
// above stay: Edit details still edits the prepared payload by field.
